import React from 'react';
import { Link } from 'react-router-dom';
import {
  Mail,
  HelpCircle,
} from 'lucide-react';
import BreadcrumbNav from '../components/common/BreadcrumbNav';
import SEOHead from '../components/common/SEOHead';
import {
  BRAND_FULL_NAME,
  BRAND_NAME,
  CONTACT_EMAIL,
  SITE_URL,
} from '../config/brand';

const faqs = [
  {
    q: 'ما هي منصة أوائل مصر؟',
    a: 'منصة تعليمية أونلاين لكل طلاب مصر بمختلف المراحل الدراسية — ابتدائي وإعدادي وثانوي — في كل المواد.',
  },
  {
    q: 'كيف أسجّل في كورس؟',
    a: 'أنشئ حساباً مجاناً، اختر الصف والكورس المناسب، ثم أكمل الاشتراك عبر الدفع أو كود التفعيل.',
  },
  {
    q: 'هل يمكن لولي الأمر متابعة تقدّم الطالب؟',
    a: 'نعم، عبر صفحة متابعة ولي الأمر بإدخال رقم هاتف ولي الأمر ورقم هاتف الطالب المسجّل.',
  },
  {
    q: 'ما سياسة الاسترجاع؟',
    a: 'المنتجات رقمية؛ الاسترجاع محدود للحالات التقنية أو التحصيل الخاطئ وفق سياسة الاسترجاع المنشورة.',
  },
];

const ContactPage: React.FC = () => {
  const breadcrumbs = [{ name: 'تواصل معنا', path: '/contact' }];

  const contactJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: `تواصل معنا | ${BRAND_NAME}`,
    url: `${SITE_URL}/contact`,
    description: `تواصل مع فريق ${BRAND_FULL_NAME} — دعم الطلاب وأولياء الأمور.`,
    mainEntity: {
      '@type': 'EducationalOrganization',
      name: BRAND_FULL_NAME,
      url: SITE_URL,
      email: CONTACT_EMAIL,
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50/40 via-white to-gold-50/20" dir="rtl">
      <SEOHead
        title={`تواصل معنا | ${BRAND_NAME}`}
        description={`تواصل مع منصة أوائل مصر عبر البريد الإلكتروني. دعم الطلاب وأولياء الأمور.`}
        keywords={`تواصل أوائل مصر, دعم الطلاب`}
        url="/contact"
        breadcrumbs={breadcrumbs}
        structuredData={[contactJsonLd, faqJsonLd]}
      />

      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8">
        <BreadcrumbNav items={breadcrumbs} />

        <header className="mb-8 text-center sm:mb-10 sm:text-right">
          <h1 className="mb-3 text-2xl font-bold text-green-900 sm:text-3xl lg:text-4xl font-cairo-bold">
            تواصل معنا
          </h1>
          <p className="mx-auto max-w-2xl text-sm leading-relaxed text-gray-600 sm:mx-0 sm:text-base">
            فريق {BRAND_FULL_NAME} جاهز لمساعدتك في التسجيل، الاشتراك، ومتابعة التعلّم.
          </p>
        </header>

        <section className="mb-10 rounded-2xl border border-green-100 bg-white p-5 sm:p-6">
          <div className="mb-4 flex items-center gap-2">
            <Mail className="h-5 w-5 text-green-700" />
            <h2 className="text-lg font-bold text-green-900 sm:text-xl">البريد والدعم</h2>
          </div>
          <p className="mb-2 text-sm text-gray-600 sm:text-base">
            راسلنا على{' '}
            <a className={`font-semibold text-green-800 underline`} href={`mailto:${CONTACT_EMAIL}`}>
              {CONTACT_EMAIL}
            </a>
          </p>
          <p className="text-sm text-gray-600 sm:text-base">
            فريق الدعم بيرد في أقرب وقت خلال ساعات العمل الرسمية.
          </p>
        </section>

        <section>
          <div className="mb-4 flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-gold-600" />
            <h2 className="text-lg font-bold text-green-900 sm:text-xl">أسئلة شائعة</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((item) => (
              <details
                key={item.q}
                className="group rounded-xl border border-green-100 bg-white p-4 open:shadow-sm"
              >
                <summary className="cursor-pointer list-none text-sm font-semibold text-green-900 sm:text-base">
                  {item.q}
                </summary>
                <p className="mt-2 text-sm leading-relaxed text-gray-600">{item.a}</p>
              </details>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-3 text-sm">
            <Link to="/refund-policy" className="text-green-800 underline hover:text-gold-600">
              سياسة الاسترجاع
            </Link>
            <Link to="/privacy" className="text-green-800 underline hover:text-gold-600">
              سياسة الخصوصية
            </Link>
            <Link to="/terms" className="text-green-800 underline hover:text-gold-600">
              الشروط والأحكام
            </Link>
            <Link to="/parent-monitoring" className="text-green-800 underline hover:text-gold-600">
              متابعة ولي الأمر
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ContactPage;
